/**
 * Paginated file browser with org / type / search / orphans filters.
 *
 * Extracted verbatim from StorageFilesTab.jsx during the Phase 6 refactor
 * (no behaviour change).
 */
import { Trash2, Filter, Search, AlertCircle, FileWarning } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { formatBytes } from '../../../utils/uploadImage';
import { shortType, colorForType } from './storageMeta';

export function FileBrowser({
  files, filesTotal, usage, allTypes, canDelete,
  orgFilter, setOrgFilter, typeFilter, setTypeFilter,
  search, setSearch, orphansOnly, setOrphansOnly, skip, setSkip,
  onSearch, onCleanupClick, onDeleteClick,
}) {
  return (
    <section className="rounded-lg border overflow-hidden">
      <div className="px-4 py-3 border-b flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold flex items-center gap-2"><Filter className="w-4 h-4" />Files</h3>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger className="w-44" data-testid="storage-org-filter"><SelectValue placeholder="All orgs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All organizations</SelectItem>
              {usage.map(o => <SelectItem key={o.org_id} value={o.org_id}>{o.org_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44" data-testid="storage-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All content types</SelectItem>
              {allTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              placeholder="Search filename…"
              className="pl-9 w-56"
              data-testid="storage-search"
            />
          </div>
          <Button variant={orphansOnly ? 'default' : 'outline'} size="sm" onClick={() => setOrphansOnly(o => !o)} data-testid="storage-orphans-toggle">
            <FileWarning className="w-4 h-4 mr-2" />Orphans only
          </Button>
          <Button size="sm" variant="outline" onClick={onCleanupClick} data-testid="storage-cleanup-btn">
            <Trash2 className="w-4 h-4 mr-2" />Clean orphans
          </Button>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="text-left p-3">Filename</th>
            <th className="text-left p-3">Type</th>
            <th className="text-left p-3 tabular-nums">Size</th>
            <th className="text-left p-3 hidden md:table-cell">Org</th>
            <th className="text-left p-3 hidden lg:table-cell">Module</th>
            <th className="text-left p-3 hidden xl:table-cell">Uploaded by</th>
            {canDelete && <th className="p-3 w-12"></th>}
          </tr>
        </thead>
        <tbody>
          {files.length === 0 ? (
            <tr><td colSpan={canDelete ? 7 : 6} className="p-8 text-center text-muted-foreground italic">No files found</td></tr>
          ) : files.map(f => (
            <tr key={f.file_id} className={`border-t ${f.is_orphan ? 'bg-amber-50' : ''}`} data-testid={`storage-file-${f.file_id}`}>
              <td className="p-3 font-medium">
                <div className="flex items-center gap-2">
                  {f.is_orphan && <AlertCircle className="w-4 h-4 text-amber-600" title="Orphan" />}
                  <span className="truncate max-w-[260px]">{f.filename}</span>
                </div>
              </td>
              <td className="p-3 text-xs">
                <span className="px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: colorForType(f.content_type) }}>
                  {shortType(f.content_type)}
                </span>
              </td>
              <td className="p-3 tabular-nums">{formatBytes(f.size_bytes)}</td>
              <td className="p-3 hidden md:table-cell">{f.org_name || '—'}</td>
              <td className="p-3 hidden lg:table-cell">{f.module_title || (f.module_id ? <span className="text-amber-600">deleted module</span> : '—')}</td>
              <td className="p-3 hidden xl:table-cell">{f.uploaded_by_email || '—'}</td>
              {canDelete && (
                <td className="p-3 text-right">
                  <Button
                    size="sm" variant="ghost"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => onDeleteClick(f)}
                    data-testid={`storage-delete-${f.file_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {filesTotal > 50 && (
        <div className="flex items-center justify-between p-3 border-t text-sm">
          <span className="text-muted-foreground">{skip + 1}–{Math.min(skip + 50, filesTotal)} of {filesTotal}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(s => Math.max(0, s - 50))}>Previous</Button>
            <Button size="sm" variant="outline" disabled={skip + 50 >= filesTotal} onClick={() => setSkip(s => s + 50)}>Next</Button>
          </div>
        </div>
      )}
    </section>
  );
}
